/**
 * @file Contributor UI — fuelMixReferenceAutofill.
 * Shared fuel mix → density / calorific autofill (Form A1, A2, …).
 * Data is loaded once per page; handlers read from refs filled at init.
 */

import axiosClient from "../../../../../api/axiosClient";
import { API_URLS } from "../../../../../constants/apiUrls";
import {
  FormDataResponse,
  FormSubSection,
} from "../../../../../interfaces/formInterfaces";
import type { FuelDensityApiRow } from "../../../../../services/a1Service";
import type { CalorificValuePublic } from "../../../../../services/calorificValuesPublicService";
import {
  classifyA1FuelDensityUnit,
  resolveA1UnitLabelFromValue,
} from "../../../../../utils/a1FuelEmbodiedEnergy";

/** Exported const — normalize Fuel Key. */
export const normalizeFuelKey = (v?: string) =>
  v ? v.toLowerCase().replace(/\s+/g, "_") : "";

/** Exported function — normalize Unit Token. */
export function normalizeUnitToken(s: unknown) {
  const raw = String(s ?? "").trim().toLowerCase();
  if (!raw) return "";
  const fixed = raw
    .replaceAll("co₂", "co2")
    .replaceAll("₂", "2")
    .replace(/\s+/g, "");
  return fixed.replace(/[^a-z0-9/()+\-.]/g, "");
}

/** e.g. prefix `fuelMix_extraction` or `fuelMix_01` → code + row from input name. */
export function parseFuelMixFieldNameWithPrefix(
  fieldNamePrefix: string,
  name: string
): { code: string; row: string } | null {
  const escaped = fieldNamePrefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const m = name.match(new RegExp(`^${escaped}_(.+)_(row\\d+)$`));
  if (!m) return null;
  return { code: m[1], row: m[2] };
}

/** Exported function — find Fuel Density For Mix. */
export function findFuelDensityForMix(
  mixValue: string,
  mixDropdownLabel: string,
  rows: FuelDensityApiRow[]
): FuelDensityApiRow | null {
  const v = String(mixValue ?? "").trim();
  if (!v) return null;

  const byId = rows.find((r) => String(r.id) === v);
  if (byId) return byId;

  const labelNorm = normalizeFuelKey(mixDropdownLabel);
  const valueNorm = normalizeFuelKey(v);

  for (const r of rows) {
    const fnKey = normalizeFuelKey(r.fuelName);
    if (fnKey && (fnKey === valueNorm || fnKey === labelNorm)) return r;
    const fnLower = r.fuelName.trim().toLowerCase();
    if (fnLower === v.toLowerCase()) return r;
    if (labelNorm && (fnKey.includes(labelNorm) || labelNorm.includes(fnKey)))
      return r;
    if (valueNorm && (fnKey.includes(valueNorm) || valueNorm.includes(fnKey)))
      return r;
  }
  return null;
}

/** Exported function — get Fuel Mix Dropdown Label For Field. */
export function getFuelMixDropdownLabelForField(
  formData: FormDataResponse | null,
  sectionCode: string,
  fuelMixFieldName: string,
  mixValue: string
): string {
  const fuelSec = formData?.formSubSections?.find(
    (s) => s.code === sectionCode
  );
  const field = fuelSec?.fields?.find((f) => f.name === fuelMixFieldName);
  const opts = field?.dropdown?.options || field?.dropdownValues || [];
  const hit = opts.find(
    (o: { value?: string | number; label?: string }) =>
      String(o.value) === String(mixValue)
  );
  return String(hit?.label ?? hit?.value ?? mixValue ?? "");
}

/** Exported function — find Calorific Value For Mix. */
export function findCalorificValueForMix(
  mixValue: string,
  mixDropdownLabel: string,
  rows: CalorificValuePublic[]
): CalorificValuePublic | null {
  const v = String(mixValue ?? "").trim();
  if (!v) return null;

  const byId = rows.find((r) => String(r.id) === v);
  if (byId) return byId;

  const labelNorm = normalizeFuelKey(mixDropdownLabel);
  const valueNorm = normalizeFuelKey(v);

  for (const r of rows) {
    const fmKey = normalizeFuelKey(r.fuelMix);
    if (fmKey && (fmKey === valueNorm || fmKey === labelNorm)) return r;
    const fmLower = r.fuelMix.trim().toLowerCase();
    if (fmLower === v.toLowerCase()) return r;
    if (labelNorm && (fmKey.includes(labelNorm) || labelNorm.includes(fmKey)))
      return r;
    if (valueNorm && (fmKey.includes(valueNorm) || valueNorm.includes(fmKey)))
      return r;
  }
  return null;
}

/** Type definition — Unit Select Option. */
export interface UnitSelectOption {
  value: string;
  label: string;
}

/** CMS base field names for fuel density unit dropdowns (A1, A2, A3). */
export function isFuelDensityUnitFormFieldName(fieldName: string): boolean {
  const n = String(fieldName ?? "").toLowerCase();
  if (!n.includes("unit")) return false;
  return (
    n === "fueldensity_unit_extraction" ||
    n === "fueldensity_unit" ||
    ((n.includes("fueldensity") || n.includes("fuelconsumptiondensity")) &&
      n.includes("unit"))
  );
}

/** Exported function — is Kg Per M3Fuel Density Unit Option. */
export function isKgPerM3FuelDensityUnitOption(opt: UnitSelectOption): boolean {
  const label = String(opt.label ?? "");
  const n = normalizeUnitToken(label);
  if (n.includes("kg") && n.includes("m3")) return true;
  return /kg\s*\/?\s*m[³3]/i.test(label);
}

/** Fuel density unit dropdowns: kg/m³ only (hide kg/L, g/cm³, etc.). */
export function filterFuelDensityUnitOptionsToKgPerM3(
  options: UnitSelectOption[]
): UnitSelectOption[] {
  const filtered = options.filter(isKgPerM3FuelDensityUnitOption);
  if (filtered.length > 0) return filtered;
  const m3Id = pickFuelDensityUnitFromOptions(options, "m3");
  if (!m3Id) return [];
  const hit = options.find((o) => String(o.value) === String(m3Id));
  return hit ? [hit] : [];
}

/** Exported function — load Fuel Field Unit Options. */
export async function loadFuelFieldUnitOptions(
  fuelSub: FormSubSection | undefined,
  fieldName: string
): Promise<UnitSelectOption[]> {
  const f = fuelSub?.fields?.find((x) => x.name === fieldName);
  if (!f) return [];

  if (f.type === "UNIT" && f.unitPropertyId) {
    try {
      const res = await axiosClient.get(
        API_URLS.UNITS.BY_PROPERTY(f.unitPropertyId)
      );
      const raw = res.data?.data;
      const units = Array.isArray(raw) ? raw : [];
      const mapped = units
        .filter((u: { id?: unknown }) => u?.id != null)
        .map((u: Record<string, unknown>) => {
          const sym = u.symbol ?? u.unitSymbol;
          const name = u.name ?? u.displayName;
          const label =
            (typeof sym === "string" && sym) ||
            (typeof name === "string" && name) ||
            String(u.id);
          return { value: String(u.id), label: String(label).trim() };
        });
      return isFuelDensityUnitFormFieldName(fieldName)
        ? filterFuelDensityUnitOptionsToKgPerM3(mapped)
        : mapped;
    } catch {
      return [];
    }
  }

  const opts =
    f.dropdown?.options ||
    f.dropdownValues ||
    ([] as { value?: string | number; label?: string }[]);
  const mapped = opts.map((o) => ({
    value: String(o.value ?? ""),
    label: String(o.label ?? o.value ?? ""),
  }));
  return isFuelDensityUnitFormFieldName(fieldName)
    ? filterFuelDensityUnitOptionsToKgPerM3(mapped)
    : mapped;
}

/** Exported function — pick Fuel Density Unit From Options. */
export function pickFuelDensityUnitFromOptions(
  options: UnitSelectOption[],
  mode: "liter" | "m3"
): string {
  if (!options.length) return "";

  const literRe =
    /(kg\s*\/?\s*l|kilogram.*lit|per\s*l\b|litre|liter|^\s*l\s*$)/i;
  const m3Re =
    /(kg\s*\/?\s*m[³3]|kg\/m3|m[³3]\b|per\s*m[³3]|cubic\s*met|cubic\s*meter)/i;

  if (mode === "liter") {
    let hit = options.find((o) => literRe.test(o.label));
    if (hit) return hit.value;
    for (const o of options) {
      const n = normalizeUnitToken(o.label);
      if (n.includes("kg") && n.includes("l") && !n.includes("m3")) {
        return o.value;
      }
    }
    return "";
  }

  let hit = options.find((o) => m3Re.test(o.label));
  if (hit) return hit.value;
  for (const o of options) {
    const n = normalizeUnitToken(o.label);
    if (
      (n.includes("kg") && n.includes("m3")) ||
      n.includes("m3") ||
      /cubic/.test(o.label.toLowerCase())
    ) {
      return o.value;
    }
  }
  return "";
}

/** Default fuel density display: kg/m³ only (Forms A1, A2, A3). */
export function fuelDensityValueAndUnitFromCatalogRow(
  row: FuelDensityApiRow,
  densityUnitOptions: UnitSelectOption[]
): { densityStr: string; unitVal: string } {
  const kgPerM3Options = filterFuelDensityUnitOptionsToKgPerM3(
    densityUnitOptions
  );
  const unitM3 = pickFuelDensityUnitFromOptions(kgPerM3Options, "m3");
  if (unitM3) {
    return {
      densityStr: String(row.densityKgM3 ?? "").trim(),
      unitVal: unitM3,
    };
  }
  return { densityStr: "", unitVal: "" };
}

function roundFuelDensity(n: number): string {
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * 1000) / 1000);
}

/** Convert stored fuel density when the contributor changes density unit (kg/L ↔ kg/m³). */
export function convertFuelDensityValueForUnitChange(
  densityRaw: string,
  previousUnitValue: string,
  newUnitValue: string,
  unitOptions: UnitSelectOption[]
): string {
  const trimmed = String(densityRaw ?? "").replace(/,/g, "").trim();
  if (!trimmed) return trimmed;

  const density = Number(trimmed);
  if (!Number.isFinite(density) || density <= 0) return trimmed;

  const prevLabel = resolveA1UnitLabelFromValue(
    previousUnitValue,
    unitOptions
  );
  const newLabel = resolveA1UnitLabelFromValue(newUnitValue, unitOptions);
  const from = classifyA1FuelDensityUnit(prevLabel);
  const to = classifyA1FuelDensityUnit(newLabel);

  if (from === to || from === "unknown" || to === "unknown") {
    return trimmed;
  }
  if (from === "kg_per_l" && to === "kg_per_m3") {
    return roundFuelDensity(density * 1000);
  }
  if (from === "kg_per_m3" && to === "kg_per_l") {
    return roundFuelDensity(density / 1000);
  }
  return trimmed;
}

/** Map a fuel-density unit field key to its paired numeric density field key. */
export function pairedFuelDensityValueKey(unitFieldName: string): string | null {
  if (unitFieldName.includes("fuelDensity_unit_extraction_")) {
    return unitFieldName.replace(
      "fuelDensity_unit_extraction_",
      "fuelDensity_extraction_"
    );
  }
  if (unitFieldName.includes("fuelDensity_unit_")) {
    return unitFieldName.replace("fuelDensity_unit_", "fuelDensity_");
  }
  return null;
}

/** Exported function — apply Fuel Density Unit Field Change. */
export function applyFuelDensityUnitFieldChange(
  prev: Record<string, string>,
  unitFieldName: string,
  newUnitValue: string,
  densityKey: string,
  unitOptions: UnitSelectOption[]
): Record<string, string> {
  const previousUnit = prev[unitFieldName] ?? "";
  const densityRaw = prev[densityKey] ?? "";
  const converted = convertFuelDensityValueForUnitChange(
    densityRaw,
    previousUnit,
    newUnitValue,
    unitOptions
  );
  return {
    ...prev,
    [unitFieldName]: newUnitValue,
    [densityKey]: converted,
  };
}

function optionListAcceptsValue(
  options: UnitSelectOption[],
  value: string
): boolean {
  if (!value) return false;
  return options.some((o) => String(o.value) === String(value));
}

/** Exported function — pick Calorific Unit From Options. */
export function pickCalorificUnitFromOptions(
  apiUnit: string,
  options: UnitSelectOption[],
  tokenMap: Record<string, number>
): string {
  const u = String(apiUnit ?? "").trim();
  if (!u) return "";

  if (options.length) {
    const target = normalizeUnitToken(u);
    for (const o of options) {
      const lbl = normalizeUnitToken(o.label);
      const val = normalizeUnitToken(o.value);
      if (target && (lbl === target || val === target)) return o.value;
      if (
        target &&
        lbl &&
        (lbl.includes(target) || target.includes(lbl))
      ) {
        return o.value;
      }
    }
    const lower = u.toLowerCase();
    for (const o of options) {
      const lbl = o.label.toLowerCase();
      if (
        lbl === lower ||
        lbl.includes(lower) ||
        lower.includes(lbl)
      ) {
        return o.value;
      }
    }
  }

  const tryId = (idStr: string) =>
    idStr && (!options.length || optionListAcceptsValue(options, idStr))
      ? idStr
      : "";

  for (const part of u
    .split(/[/\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean)) {
    const p = part.toLowerCase();
    const id = tokenMap[p] ?? tokenMap[normalizeUnitToken(p)];
    if (id != null) {
      const s = tryId(String(id));
      if (s) return s;
    }
  }
  const id2 =
    tokenMap[u.toLowerCase()] ?? tokenMap[normalizeUnitToken(u)];
  if (id2 != null) {
    const s = tryId(String(id2));
    if (s) return s;
  }
  return "";
}
